import {
    AfterViewInit,
    Component,
    computed,
    effect,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FilterRequest } from '@app/shared/models/ecmr/filter-request';
import { FormControl, FormGroup } from '@angular/forms';
import { EcmrStatus } from '@app/shared/models/ecmr/EcmrStatus';
import { EcmrService } from '@app/core/services/ecmr-services/ecmr.service';
import { DateFormatService } from '@app/shared/services/date-format.service';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { EcmrPage } from '@app/shared/models/ecmr/EcmrPage';
import { lastValueFrom, Subject } from 'rxjs';
import { EcmrType } from '@app/shared/models/ecmr/EcmrType';
import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { EcmrDetailDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-detail-dialog/ecmr-detail-dialog.component';
import { EcmrTransportType } from '@app/shared/models/ecmr/EcmrTransportType';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { SearchStore } from '@app/stores/searchStore';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/shared/material/material.module';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { EcmrStatusComponent } from '@app/shared/components/ecmr/subcomponents/ecmr-status/ecmr-status.component';
import { LatestMotorPlatePipe } from '@app/shared/pipes/latest-motor-plate.pipe';
import { isEcmrDcaCompliant } from '@app/shared/components/ecmr/ecmr-dca.util';
import {
    ECMR_COLUMNS,
    ecmrColumnPreferences,
} from '@app/shared/components/ecmr/ecmr-table-columns';
import { TablePreferencesStore } from '@app/stores/tablePreferencesStore';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-archive-ecmr',
    templateUrl: './archive-ecmr.component.html',
    styleUrl: './archive-ecmr.component.scss',
    imports: [
        CommonModule,
        MaterialModule,
        TranslateModule,
        ReactiveFormsModule,
        EcmrStatusComponent,
        LatestMotorPlatePipe,
    ],
})
export class ArchiveEcmrComponent implements OnInit, AfterViewInit, OnDestroy {
    baseRoute = input<string>();
    isReadOnly = input(false);
    shouldReloadTable = input<Subject<boolean>>();
    searchControl = input<FormControl>();

    dataSource: MatTableDataSource<Ecmr> = new MatTableDataSource<Ecmr>();
    intervalId: ReturnType<typeof setInterval>;
    private viewReady = false;

    paginator = viewChild.required(MatPaginator);
    sort = viewChild.required(MatSort);

    private columnPreferences = ecmrColumnPreferences('archive');

    // Column order (drag & drop). Which columns are visible is chosen via
    // Show/Hide Columns in the paginated-table-filter.
    columnOrder = signal<string[]>([
        'id',
        ...ECMR_COLUMNS.map(column => String(column.key)),
    ]);
    private visibleColumns = computed(() =>
        this.tablePreferencesStore.getVisibleColumns(
            this.sessionService.getSession()._key,
            this.columnPreferences.viewKey,
            this.columnPreferences.columns.map(column => String(column.key)),
            this.columnPreferences.defaultHidden
        )
    );
    displayedColumns = computed(() =>
        this.columnOrder().filter(
            column => column === 'id' || this.visibleColumns().includes(column)
        )
    );

    filterRequest: FilterRequest = {
        ecmrId: null,
        referenceId: '',
        from: '',
        to: '',
        transportType: null,
        status: null,
        licensePlate: '',
        carrierName: '',
        carrierPostCode: '',
        consigneePostCode: '',
        lastEditor: '',
        createdAt: '',
        editedAt: '',
        groupId: null,
        transportId: null,
    };

    filterFormGroup = new FormGroup({
        referenceId: new FormControl<string | null>(null),
        from: new FormControl<string | null>(null),
        to: new FormControl<string | null>(null),
        transportType: new FormControl<EcmrTransportType | null>(null),
        status: new FormControl<EcmrStatus | null>(null),
        licensePlate: new FormControl<string | null>(null),
        carrierName: new FormControl<string | null>(null),
        carrierPostCode: new FormControl<string | null>(null),
        consigneePostCode: new FormControl<string | null>(null),
        lastEditor: new FormControl<string | null>(null),
    });

    protected readonly EcmrStatus = EcmrStatus;
    protected readonly EcmrTransportType = EcmrTransportType;

    private searchStore = inject(SearchStore);
    private tablePreferencesStore = inject(TablePreferencesStore);

    constructor(
        private ecmrService: EcmrService,
        protected dateFormatService: DateFormatService,
        private sessionService: SessionService,
        private router: Router,
        private toastr: ToastrService,
        private dialog: MatDialog
    ) {
        // Listen to search state changes from the store
        effect(() => {
            const query = this.searchStore.searchText();
            this.filterRequest.referenceId = query;
            this.filterRequest.from = query;
            this.filterRequest.to = query;
            this.filterRequest.carrierName = query;
            this.filterRequest.carrierPostCode = query;
            this.filterRequest.licensePlate = query;
            this.filterRequest.lastEditor = query;
            this.filterRequest.consigneePostCode = query;
            this.filterRequest.createdAt = query;
            this.filterRequest.editedAt = query;

            if (query !== null && query.trim() !== '') {
                const enumQuery = query.trim().toLowerCase();
                this.filterRequest.status =
                    Object.values(EcmrStatus).find(
                        status =>
                            enumQuery !== '' &&
                            status.toLowerCase().startsWith(enumQuery)
                    ) || null;
                this.filterRequest.transportType =
                    Object.values(EcmrTransportType).find(
                        type =>
                            enumQuery !== '' &&
                            type.toLowerCase().startsWith(enumQuery)
                    ) || null;
            } else {
                this.filterRequest.status = null;
                this.filterRequest.transportType = null;
            }

            this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        });
    }

    ngOnInit() {
        const savedColumns = this.ecmrService.getDisplayedColumns();
        if (savedColumns) {
            this.columnOrder.set(savedColumns);
        }
    }

    async ngAfterViewInit() {
        this.viewReady = true;
        this.startRefreshInterval();
    }

    ngOnDestroy() {
        clearInterval(this.intervalId);
    }

    async loadEcmrs(siteKey: string) {
        if (!this.viewReady) {
            return;
        }

        const sort = this.sort();
        const paginator = this.paginator();

        let page: EcmrPage;
        if (sort.active) {
            page = await lastValueFrom(
                this.ecmrService.getAllEcmr(
                    this.filterRequest,
                    siteKey,
                    EcmrType.ARCHIVED,
                    paginator.pageIndex,
                    paginator.pageSize,
                    sort.active,
                    sort.direction.toUpperCase()
                )
            );
        } else {
            page = await lastValueFrom(
                this.ecmrService.getAllEcmr(
                    this.filterRequest,
                    this.sessionService.currentOrganizationSiteKey(),
                    EcmrType.ARCHIVED,
                    paginator.pageIndex,
                    paginator.pageSize,
                    'creationDate',
                    'ASC'
                )
            );
        }

        this.dataSource.data = page.ecmrs;
        paginator.length = page.totalElements;
    }

    async startRefreshInterval() {
        await this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        clearInterval(this.intervalId);
        this.intervalId = setInterval(() => {
            this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        }, 60 * 1000);
    }

    sortAndUpdateData() {
        this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
    }

    sortPredicate(index: number, item: CdkDrag<number>) {
        return index != 0;
    }

    showHistoryOfEcmr(ecmrId: string, refId: string) {
        this.router.navigate([
            this.baseRoute() +
                '/' +
                RoutingEndpoints.ecmr_history +
                `/${ecmrId}/${refId}`,
        ]);
    }

    //DragDrop function to move and save columns
    tableDrop(event: CdkDragDrop<string[]>) {
        const displayed = this.displayedColumns();
        const order = [...this.columnOrder()];
        const previousIndex = order.indexOf(displayed[event.previousIndex]);
        const currentIndex = order.indexOf(displayed[event.currentIndex]);
        // the id column stays first
        if (previousIndex > 0 && currentIndex > 0) {
            moveItemInArray(order, previousIndex, currentIndex);
            this.columnOrder.set(order);
            this.ecmrService.saveDisplayedColumns(order);
        }
    }

    public getTransportType(ecmr: Ecmr): EcmrTransportType | null {
        return this.ecmrService.getTransportType(ecmr);
    }

    /**
     * DeCA badge shows once the sender has signed - the backend leaves the
     * status at NEW until then - and the tax numbers make the consignment
     * conform.
     */
    isDcaCompliant(ecmr: Ecmr): boolean {
        return (
            ecmr.ecmrStatus !== EcmrStatus.NEW &&
            isEcmrDcaCompliant(ecmr.ecmrConsignment)
        );
    }

    showDetailsForEcmrAtIndex(index: number) {
        const dialogRef = this.dialog.open(EcmrDetailDialogComponent, {
            width: '80vw',
            height: '80vh',
            disableClose: false,
            data: {
                baseRoute: this.baseRoute(),
                ecmr: this.dataSource.data[index],
                isArchive: true,
                isReadOnly: this.isReadOnly(),
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadEcmrs(
                    this.sessionService.currentOrganizationSiteKey()
                );
            }
        });
    }
}
